import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { RefreshCw, Edit, Trash2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { getTasks, linkQuotationToTask, getImageUrl } from '@/utils/api';

// Format a saved quotation's total in its authoring currency. Falls back to
// INR when the field is missing (legacy rows). Uses ASCII-safe "INR"/"USD"
// prefixes since this table may also be screenshot/printed.
function formatSavedTotal(q) {
  const ccy = (q.currency || 'INR').toUpperCase();
  const value = ccy === 'USD' ? (q.final_cost_usd || 0) : (q.final_cost_inr || 0);
  const locale = ccy === 'USD' ? 'en-US' : 'en-IN';
  const symbol = ccy === 'USD' ? '$' : '₹';
  return symbol + value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function QuotationHistoryTab({ savedQuotations, loadingQuotations, fetchSavedQuotations, loadSavedQuotation, handleDeleteQuotation }) {
  const [linkDialogFor, setLinkDialogFor] = useState(null); // quotation object
  const [linkTasks, setLinkTasks] = useState([]);
  const [linkTaskId, setLinkTaskId] = useState('');
  const [linkSearch, setLinkSearch] = useState('');
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (!linkDialogFor) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await getTasks();
        if (!cancelled) setLinkTasks(res.data || []);
      } catch (err) {
        console.error('Failed to load tasks for link dialog', err);
        toast.error('Could not load tasks');
      }
    })();
    return () => { cancelled = true; };
  }, [linkDialogFor]);

  const filteredTasks = linkSearch
    ? linkTasks.filter(t => {
        const s = linkSearch.toLowerCase();
        return (t.internal_cad_no || '').toLowerCase().includes(s)
          || (t.party_cad_no || '').toLowerCase().includes(s)
          || (t.style_no || '').toLowerCase().includes(s)
          || (t.client_name || '').toLowerCase().includes(s);
      })
    : linkTasks.slice(0, 100);

  const handleConfirmLink = async () => {
    if (!linkTaskId) { toast.error('Pick a task first'); return; }
    setLinking(true);
    try {
      await linkQuotationToTask(linkDialogFor.id, { task_id: linkTaskId });
      toast.success('Quotation linked to task');
      setLinkDialogFor(null);
      setLinkTaskId('');
      setLinkSearch('');
      fetchSavedQuotations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Link failed');
    } finally {
      setLinking(false);
    }
  };

  return (
    <Card>
      <CardHeader className="py-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base">Saved Quotations</CardTitle>
        <Button variant="outline" size="sm" onClick={fetchSavedQuotations} disabled={loadingQuotations} data-testid="qt-refresh-history">
          <RefreshCw className={`w-4 h-4 mr-2 ${loadingQuotations ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ref</TableHead><TableHead>Style No</TableHead><TableHead>Ver</TableHead>
              <TableHead>Date</TableHead><TableHead>Client</TableHead><TableHead>Piece</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>CAD</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadingQuotations ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
            ) : savedQuotations.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">No saved quotations yet</TableCell></TableRow>
            ) : savedQuotations.map(q => {
              const ccy = (q.currency || 'INR').toUpperCase();
              return (
                <TableRow key={q.id} data-testid={`qt-history-row-${q.id}`}>
                  <TableCell className="font-mono text-xs">
                    <div className="flex items-center gap-2">
                      {q.image_url && <img src={getImageUrl(q.image_url)} alt="" className="h-6 w-6 rounded object-cover border" />}
                      <span>{q.ref}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs font-medium">{q.style_no || '—'}</TableCell>
                  <TableCell className="text-xs text-center">{q.version || '—'}</TableCell>
                  <TableCell className="text-sm">{new Date(q.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="font-medium">{q.client_name}</TableCell>
                  <TableCell className="capitalize">{q.piece_type}</TableCell>
                  <TableCell className="text-right font-medium text-amber-600">
                    {formatSavedTotal(q)}
                    <span className="text-[10px] text-slate-400 ml-1">{ccy}</span>
                  </TableCell>
                  <TableCell>
                    {q.task_id ? (
                      <span className="text-[11px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">Linked</span>
                    ) : (
                      <Button variant="ghost" size="sm" className="h-6 text-[11px] text-amber-700 hover:bg-amber-50" onClick={() => setLinkDialogFor(q)} data-testid={`qt-history-link-${q.id}`}>
                        <Link2 className="w-3 h-3 mr-1" /> Link to CAD
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <Button variant="ghost" size="sm" onClick={() => loadSavedQuotation(q.id)} data-testid={`qt-history-load-${q.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteQuotation(q.id)} data-testid={`qt-history-delete-${q.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>

      <Dialog open={!!linkDialogFor} onOpenChange={(open) => { if (!open) { setLinkDialogFor(null); setLinkTaskId(''); setLinkSearch(''); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="qt-link-dialog">
          <DialogHeader>
            <DialogTitle>Link {linkDialogFor?.ref} to a CAD task</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">The task's style_no will replace this quote's style_no, and the task will start showing this quotation reference.</p>
          <Input placeholder="Search by CAD no, style, or client…" value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} className="mb-2" data-testid="qt-link-search" />
          <div className="max-h-64 overflow-y-auto border rounded">
            {filteredTasks.length === 0 ? (
              <p className="p-3 text-xs text-slate-400">No tasks match</p>
            ) : (
              <Select value={linkTaskId} onValueChange={setLinkTaskId}>
                <SelectTrigger className="m-2" data-testid="qt-link-task-select"><SelectValue placeholder="Pick a task…" /></SelectTrigger>
                <SelectContent>
                  {filteredTasks.map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.internal_cad_no || t.id.slice(0, 6)}{t.style_no ? ` / ${t.style_no}` : ''} — {t.client_name || 'no client'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLinkDialogFor(null)}>Cancel</Button>
            <Button onClick={handleConfirmLink} disabled={!linkTaskId || linking} className="bg-amber-500 hover:bg-amber-600" data-testid="qt-link-confirm">{linking ? 'Linking…' : 'Link'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
